import { useEffect } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { getOperationName } from "@apollo/client/utilities";
import { SUBSCRIPTION_CONFIRMED_EVENT } from "@onmo/graphql";
import {
  completeContentViewGQL,
  getUserProfileTgpGQL,
  meGQL,
} from "../graphql";
import { USER_NOTIFICATION_SUBSCRIPTION } from "@onmo/graphql/legacyGraphql";

import { GamingStorage } from "../models/gamingStorage";

const COMPLETE_FALLBACK_DELAY = 15000;
const NOTIFICATION_OPERATION = getOperationName(USER_NOTIFICATION_SUBSCRIPTION);

export const useArticleContentView = () => {
  const client = useApolloClient();
  const [completeContentView] = useMutation(completeContentViewGQL);

  useEffect(() => {
    const complete = async () => {
      const contentId = GamingStorage.getPendingArticleView();
      if (!contentId) return;
      GamingStorage.clearPendingArticleView();

      try {
        await completeContentView({ variables: { input: { contentId } } });
        client.refetchQueries({ include: [getUserProfileTgpGQL, meGQL] });
      } catch {
        // do nothing
      }
    };

    const onSubscriptionConfirmed = (event: Event) => {
      const operation = (event as CustomEvent<string>).detail;
      if (operation === NOTIFICATION_OPERATION) complete();
    };

    let fallbackId: NodeJS.Timeout | undefined;
    const scheduleFallback = () => {
      clearTimeout(fallbackId);
      fallbackId = setTimeout(complete, COMPLETE_FALLBACK_DELAY);
    };

    scheduleFallback();
    window.addEventListener(
      SUBSCRIPTION_CONFIRMED_EVENT,
      onSubscriptionConfirmed,
    );
    window.addEventListener("pageshow", scheduleFallback);
    return () => {
      window.removeEventListener(
        SUBSCRIPTION_CONFIRMED_EVENT,
        onSubscriptionConfirmed,
      );
      window.removeEventListener("pageshow", scheduleFallback);
      clearTimeout(fallbackId);
    };
  }, [completeContentView, client]);
};
